import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import {
    convertDateOnlyStringToDate,
    convertDateToDateOnlyString,
} from "@insite/client-framework/Common/Utilities/DateUtilities";
import Zone from "@insite/client-framework/Components/Zone";
import { Cart } from "@insite/client-framework/Services/CartService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import {
    canCheckoutWithCart,
    canSubmitForQuote,
    canSubmitRequisition,
    getCurrentCartState,
    hasOnlyQuoteRequiredProducts,
    hasQuoteRequiredProducts,
    isCartCheckoutDisabled,
    isCartEmpty,
    isPunchOutOrder,
} from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import clearCurrentCart from "@insite/client-framework/Store/Pages/Cart/Handlers/ClearCurrentCart";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerState } from "@insite/mobius/DatePicker/DatePicker";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import TextField from "@insite/mobius/TextField";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import submitQuote from "@tJohnstonCompanies/Handlers/SubmitQuoteHandler";
import React, { useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({});

interface OwnProps {
    cart?: Cart;
    setIsSubmitQuoteModalOpen: (val: boolean) => void;
}

const mapDispatchToProps = {
    submitQuote,
    clearCurrentCart,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface SubmitQuoteStyles {
    form?: InjectableCss;
    maincntr?: InjectableCss;
    cntr1?: InjectableCss;
    itm1?: GridItemProps;
    gridItem?: GridItemProps;
    txtfld?: InjectableCss;
    button?: InjectableCss;
    submitButton?: ButtonPresentationProps;
}

export const submitQuoteStyles: SubmitQuoteStyles = {
    itm1: {
        width: 12,
    },
    gridItem: {
        width: 6,
    },
    button: {
        css: css`
            padding: 15px;
        `,
    },
};

const styles = submitQuoteStyles;
const StyledForm = getStyledWrapper("form");
const emailRegexp = new RegExp("\\w+([-+.']\\w+)*@\\w+([-.]\\w+)*\\.\\w+([-.]\\w+)*");

const SubmitQuoteFormCustom = ({ cart, submitQuote, setIsSubmitQuoteModalOpen, clearCurrentCart }: Props) => {
    const emailFieldMessage = siteMessage("Enter a valid Email Address");
    const UserNameRequiredFieldMessage = siteMessage("Name is Required");
    const phoneNumberRequiredFieldMessage = siteMessage("Phone Number is Required");
    const emailRequiredFieldMessage = siteMessage("Email is Required");
    const validPhoneMsg = siteMessage("Enter a Valid Phone Number");
    const companyNameRequiredFieldMessage = siteMessage("Company Name is Required");

    const [userName, setuserName] = useState("");
    const [userNameError, setuserNameError] = useState(UserNameRequiredFieldMessage);
    const [email, setEmail] = useState("");
    const [emailError, setEmailError] = React.useState(emailFieldMessage);
    const [phoneNumber, setPhoneNumber] = React.useState("");
    const [phoneNumberError, setphoneNumberErrorError] = useState(validPhoneMsg);
    const [companyName, setCompanyName] = useState("");
    const [companyNameError, setCompanyNameError] = useState(companyNameRequiredFieldMessage);
    const [errorMessage, setErrorMessage] = useState("");
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const toasterContext = useContext(ToasterContext);

    const validateUserName = (name: string) => {
        const errorMessage = !name ? UserNameRequiredFieldMessage : "";
        setuserNameError(errorMessage);
        return !errorMessage;
    };

    const addDashToPhone = (value: string) => {
        if (value.length > 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}-${value.slice(6)}`;
        }
        if (value.length > 3 && value.length <= 6) {
            return `${value.slice(0, 3)}-${value.slice(3, 6)}`;
        }
        return value;
    };

    const removeDashFromPhone = (value: string) => {
        return value.replace(/\D/g, "");
    };

    const validatePhoneNumber = (value: string) => {
        value = removeDashFromPhone(value);
        const errorMessage = !value
            ? phoneNumberRequiredFieldMessage
            : Number(value) && value.length === 10
            ? ""
            : validPhoneMsg;
        setphoneNumberErrorError(errorMessage);
        return !errorMessage;
    };

    const validateEmail = (email: string) => {
        const errorMessage = !email ? emailRequiredFieldMessage : emailRegexp.test(email) ? "" : emailFieldMessage;
        setEmailError(errorMessage);
        return !errorMessage;
    };

    const validateCompanyName = (name: string) => {
        const errorMessage = !name ? companyNameRequiredFieldMessage : "";
        setCompanyNameError(errorMessage);
        return !errorMessage;
    };

    const validateSubmitEnabled = () => {
        return !(userNameError || emailError || phoneNumberError || companyNameError);
    };

    const usrNameChangeHandler = (value: string) => {
        validateUserName(value);
        setuserName(value);
    };

    const phoneNumberChangeHandler = (value: string) => {
        const regex = /[0-9]|\./;
        if (!value) {
            setPhoneNumber("");
            validatePhoneNumber(value);
        }
        value = removeDashFromPhone(value);
        if (!regex.test(value) || value.length > 10) {
            return;
        }
        if (value) {
            const formattedPhone = addDashToPhone(value);
            formattedPhone !== undefined && setPhoneNumber(formattedPhone);
            validatePhoneNumber(formattedPhone);
        }
    };

    const emailChangeHandler = (email: string) => {
        validateEmail(email);
        setEmail(email);
    };

    const companyNameChangeHandler = (value: string) => {
        validateCompanyName(value);
        setCompanyName(value);
    };

    const onSuccessHandler = () => {
        setErrorMessage("");
        toasterContext.addToast({ body: translate("Successfully submitted!"), messageType: "success" });
        setIsSubmitQuoteModalOpen(false);
        clearCurrentCart();
        window.location.href = "/ThankYou";

        clearFormFields();
    };

    // eslint-disable-next-line require-await
    const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        if (!isSubmitted) {
            setIsSubmitted(true);
        }

        if (!validateSubmitEnabled()) {
            return;
        }

        setIsSubmitting(true);

        submitQuote({
            Name: userName,
            PhoneNumber: phoneNumber,
            Email: email,
            CompanyName: companyName,
            Cart: cart as any,

            onSuccess: onSuccessHandler,
            onError: error => {
                setErrorMessage(error);
                setIsSubmitting(false);
                const errorMsg = error.toString().length > 400 ? "Something went wrong!" : error;
                toasterContext.addToast({ body: translate(errorMsg), messageType: "danger" });
            },
        });
    };

    const clearFormFields = () => {
        setuserName("");
        setEmail("");
        setPhoneNumber("");
        setCompanyName("");
        setErrorMessage("");
        setIsSubmitted(false);
        setIsSubmitting(false);
        setuserNameError(UserNameRequiredFieldMessage);
        setEmailError(emailFieldMessage);
        setphoneNumberErrorError(validPhoneMsg);
        setCompanyNameError(companyNameRequiredFieldMessage);
    };

    const isSubmitEnabled = validateSubmitEnabled();

    return (
        <>
            <StyledForm {...styles.form} onSubmit={submitHandler}>
                <GridContainer {...styles.maincntr}>
                    <GridItem {...styles.itm1}>
                        <GridContainer {...styles.cntr1}>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    {...styles.txtfld}
                                    type="text"
                                    label="Enter Your Name *"
                                    onChange={e => usrNameChangeHandler(e.currentTarget.value)}
                                    value={userName}
                                    error={isSubmitted && userNameError}
                                />
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    {...styles.txtfld}
                                    type="text"
                                    label="Enter Email Address *"
                                    onChange={e => emailChangeHandler(e.currentTarget.value)}
                                    value={email}
                                    error={isSubmitted && emailError}
                                />
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    {...styles.txtfld}
                                    type="text"
                                    label="Enter Phone Number *"
                                    onChange={e => phoneNumberChangeHandler(e.currentTarget.value)}
                                    value={phoneNumber}
                                    error={isSubmitted && phoneNumberError}
                                />
                            </GridItem>
                            <GridItem {...styles.gridItem}>
                                <TextField
                                    {...styles.txtfld}
                                    type="text"
                                    label="Enter Company Name *"
                                    onChange={e => companyNameChangeHandler(e.currentTarget.value)}
                                    value={companyName}
                                    error={isSubmitted && companyNameError}
                                />
                            </GridItem>
                            <StyledWrapper {...styles.button}>
                                <Button
                                    {...styles.submitButton}
                                    type="submit"
                                    disabled={isSubmitting || (isSubmitted && !isSubmitEnabled)}
                                    data-test-selector="createAccount_createButton"
                                >
                                    {translate("Submit")}
                                </Button>
                            </StyledWrapper>
                        </GridContainer>
                    </GridItem>
                </GridContainer>
            </StyledForm>
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(SubmitQuoteFormCustom);
